/**
 * @pdmg/reporting-bindings — the sandboxed expression evaluator.
 *
 * Bindings are JSONata evaluated against the report data model. Scope §5 of the
 * template spec makes three promises about them; this file is where they are kept:
 *
 *   1. No host access, no I/O, no globals.
 *   2. Bounded by a step and time budget — an expensive expression fails the render
 *      rather than hanging it.
 *   3. No mutation of the data model.
 *
 * (1) and (3) come free with JSONata: it has no I/O surface and no assignment into
 * its input. We only have to avoid *adding* either, which means never registering a
 * host function into an expression's environment.
 *
 * (2) uses JSONata 2.x's own guardrails (`{ timeout, stack }`) rather than the
 * `__evaluate_entry` callback recipe that circulates in older documentation. The
 * callbacks still exist but are keyed by `Symbol.for('jsonata.__evaluate_entry')` in
 * 2.x, so the string-keyed version of that recipe silently registers nothing and
 * enforces no budget at all — it looks correct and does nothing.
 *
 * ── What the spec asked for versus what exists ──
 *
 * Template spec §10.4 calls for a "step budget". JSONata does not have one. It has
 * exactly two guardrails, and they bound different things:
 *
 *   timeout — wall-clock milliseconds. This is the real cost bound.
 *   stack   — RECURSION DEPTH, not steps. `depth` is incremented on entry to each AST
 *             node and decremented on exit (jsonata.js:3511, 3604), so it measures how
 *             deep the tree currently is, never how much work has been done.
 *
 * The practical consequence is worth knowing before relying on either: `stack: 500`
 * does not stop `$map([1..100000], ...)`, because a hundred thousand iterations are
 * shallow. Only `timeout` bounds that. Conversely `timeout` will not catch a
 * non-terminating recursive lambda quickly on a loaded box, and `stack` will.
 *
 * So both are set, and neither is presented as a step count. Guardrail state is
 * per-`evaluate()` call, which is what makes caching compiled expressions safe.
 */

import jsonata from 'jsonata';

/** Budgets. Deliberately generous — these catch runaway, not merely slow. */
export interface Budget {
  /**
   * Max recursion depth. Catches a non-terminating recursive lambda. NOT a step
   * count — a wide, shallow expression can do unlimited work within any depth limit.
   */
  maxDepth: number;
  /** Max wall-clock milliseconds. This is the bound that actually limits cost. */
  maxMs: number;
}

export const DEFAULT_BUDGET: Budget = { maxDepth: 500, maxMs: 1_000 };

export class BindingError extends Error {
  constructor(
    message: string,
    readonly expression: string,
    readonly blockPath: string
  ) {
    super(message);
    this.name = 'BindingError';
  }
}

/**
 * Compiled-expression cache. Templates re-evaluate the same expression once per
 * repeat iteration — a 5,000-row venue table would otherwise re-parse the same source
 * 5,000 times.
 *
 * Keyed by budget as well as source, because the budget is bound at compile time.
 * Evaluation state (elapsed time, node count) is per-`evaluate()` call, so a cached
 * expression carries nothing between uses.
 */
const cache = new Map<string, ReturnType<typeof jsonata>>();

export function compile(src: string, budget: Budget): ReturnType<typeof jsonata> {
  const key = `${budget.maxDepth}|${budget.maxMs}|${src}`;
  const hit = cache.get(key);
  if (hit) return hit;
  // `stack`/`timeout` are compile-time options in JSONata 2.x, which is why the
  // budget has to be part of the cache key.
  const expr = jsonata(src, { timeout: budget.maxMs, stack: budget.maxDepth } as never);
  cache.set(key, expr);
  return expr;
}

/**
 * JSONata signals a tripped guardrail by throwing `{ code, value }` — a plain object,
 * not an Error, so it has no `.message`. Translate to something a render log can act
 * on.
 */
function describe(e: unknown): string {
  const err = e as { code?: string; value?: unknown; message?: string };
  if (err?.code === 'D1011') return `expression exceeded the recursion-depth budget (${String(err.value)})`;
  if (err?.code === 'D1012') return `expression exceeded the time budget (${String(err.value)}ms)`;
  if (err?.message) return err.message;
  return typeof e === 'string' ? e : JSON.stringify(e);
}

/**
 * Evaluate one binding expression.
 *
 * `scope` carries repeat variables (`$adGroup` and friends) and is the only thing
 * beyond the data model an expression can see.
 */
export async function evaluateBinding(
  src: string,
  data: unknown,
  scope: Record<string, unknown> = {},
  budget: Budget = DEFAULT_BUDGET,
  blockPath = '<unknown>'
): Promise<unknown> {
  let expr: ReturnType<typeof jsonata>;
  try {
    expr = compile(src, budget);
  } catch (e) {
    throw new BindingError(`does not parse: ${describe(e)}`, src, blockPath);
  }

  try {
    return await expr.evaluate(data, scope);
  } catch (e) {
    throw new BindingError(describe(e), src, blockPath);
  }
}

/**
 * Truthiness for `visibleWhen`, matching JSONata's own notion rather than
 * JavaScript's.
 *
 * The difference that matters: JSONata returns `undefined` for a path that matches
 * nothing, and an empty sequence for a filter that selects nothing. Both mean "no",
 * and neither is `false`. A plain `!!value` would also treat the number 0 as false,
 * which would hide a block legitimately reporting zero delivery.
 */
export function isTruthy(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.length > 0;
  if (typeof value === 'number') return true; // 0 is a measurement, not an absence
  return true;
}

/** Clear the compiled-expression cache. Test seam. */
export function _clearCache(): void {
  cache.clear();
}
